import { Card, CardContent, Grid2 as Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import ContentLoader from '@components/ContentLoader';
import DrilldownItem from '@components/DrilldownItem';
import RoundedButton from '@components/RoundedButton';

import { useGetAccount } from '@hooks/api/account';
import {
  useGetFHIRPatient,
  useUpdateFHIRPatient,
} from '@hooks/api/fhir-patient';
import { useGetScanProfile, useUpsertScanProfile } from '@hooks/api/scan';
import { useAdminChangeName, useGetUser } from '@hooks/api/user';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useCuration } from '@hooks/useCuration';
import useQuestionnaire from '@hooks/useQuestionnaire';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { getQuestionnaireResponseTemplate } from '@models/fhir-templates/questionnaire-response';
import type { FormValues as PatientFormValues } from '@models/patient';
import { DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER } from '@models/questionnaire';

import { formatPatientId } from '@utils/format';

import { usePostQuestionnaireResponses } from 'generated/questionnaire.generated';

import Header from './Header';
import DemographicsForm from './MainForm';
import QuestionnaireForm from './QuestionnaireForm';
import ScanProfileForm from './ScanProfileForm';

const messages = defineMessages({
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  group: {
    id: 'GLOBAL.group',
    defaultMessage: 'Group',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  save: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
});

export type FormValues = PatientFormValues & {
  // Lives on the User record, not the FHIR Patient — saved via a separate
  // adminChangeName mutation in handleSubmit, not through updatePatient.
  first_name: string;
  middle_name: string;
  last_name: string;
  isSmoker: boolean | null;
  isDiabetic: boolean | null;
  isHypertensive: boolean | null;
  useBpMeds: boolean | null;
  questionnaireResponses?: { [linkId: string]: any } | null;
};

interface DemographicsCardProps {
  user_id: string;
  patient_id?: string;
}

export default function DemographicsCard({
  user_id,
  patient_id,
}: DemographicsCardProps) {
  const { formatMessage } = useIntl();
  const { phoneSchema } = useCommonSchemas();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { isEditing },
    stopEditingRecord,
  } = useCuration();
  const {
    state: {
      isCurator,
      isCuratorAdmin,
      isAdmin,
      isPractitioner,
      isOrgAdmin,
      loggedInProfile,
    },
  } = useSession();

  const { data: patientData, loading: patientLoading } = useGetFHIRPatient();
  const { data: accountData, loading: accountLoading } = useGetAccount();
  const { data: userData, loading: userLoading } = useGetUser({
    id: user_id,
  });
  const { data: scanProfileData, loading: scanProfileLoading } =
    useGetScanProfile();
  const {
    data: questionnaireData,
    loading: questionnaireLoading,
    formHelpers,
  } = useQuestionnaire({
    patient: patient_id ?? '',
  });

  const [updatePatient] = useUpdateFHIRPatient();
  const [upsertScanProfile] = useUpsertScanProfile();
  const [createQuestionnaireResponse] = usePostQuestionnaireResponses();
  const [adminChangeName] = useAdminChangeName();

  const is_owner = accountData?.getAccountRecord.owner_id === user_id;
  const user = userData?.getUserRecord;
  const age = patientData?.getAge();
  // Mirrors the adminChangeName permission row: curator/curatorAdmin/admin
  // always, practitioner/orgAdmin only within their own org.
  //
  // UX gate only, AuthorizationGuard is authoritative. It accepts either the
  // child-account case or the patient sitting directly in the org account
  // (authorization.guard.ts:220-224), so both branches belong here or the
  // fields hide from practitioners the backend would have allowed.
  const isSameOrg =
    accountData?.getAccountRecord.parent_id === loggedInProfile?.account_id ||
    accountData?.getAccountRecord.account_id === loggedInProfile?.account_id;
  const canEditName =
    isCurator ||
    isCuratorAdmin ||
    isAdmin ||
    ((isPractitioner || isOrgAdmin) && isSameOrg);

  const loading =
    userLoading ||
    patientLoading ||
    accountLoading ||
    scanProfileLoading ||
    questionnaireLoading;

  const initialValues: FormValues = {
    first_name: user?.first_name ?? '',
    middle_name: user?.middle_name ?? '',
    last_name: user?.last_name ?? '',
    birthday: patientData?.birthday ?? '',
    sex: patientData?.sex ?? '',
    gender: patientData?.gender ?? '',
    phones: patientData?.phones ?? [],
    // Primary address
    address: patientData?.address ?? '',
    address2: patientData?.address2 ?? '',
    postalCode: patientData?.postalCode ?? '',
    city: patientData?.city ?? '',
    region: patientData?.region ?? '',
    country: patientData?.country ?? 'CA',
    // Secondary address
    secondAddress: patientData?.secondAddress ?? '',
    secondAddressUnit: patientData?.secondAddressUnit ?? '',
    secondPostalCode: patientData?.secondPostalCode ?? '',
    secondCity: patientData?.secondCity ?? '',
    secondRegion: patientData?.secondRegion ?? '',
    secondCountry: patientData?.secondCountry ?? '',
    // Scan demographics
    isSmoker: scanProfileData?.is_smoker ?? null,
    isDiabetic: scanProfileData?.is_diabetic ?? null,
    isHypertensive: scanProfileData?.is_hypertensive ?? null,
    useBpMeds: scanProfileData?.has_blood_pressure_medication ?? null,
    // Demographics questionnaire
    questionnaireResponses: formHelpers.initialValues,
    submit: null,
  };

  const validationSchema: Schema<Partial<FormValues>> = object().shape({
    // Matches ChangeName.tsx's self-service validation: first/last
    // required, middle optional. trim() runs as a cast before required(), so a
    // whitespace-only name fails here as a field error instead of reaching the
    // backend and coming back as a generic toast.
    first_name: string().trim().required(formatMessage(messages.required)),
    middle_name: string().trim(),
    last_name: string().trim().required(formatMessage(messages.required)),
    birthday: string().required(formatMessage(messages.required)),
    sex: string().nullable(),
    gender: string().nullable(),
    // Primary address
    address: string(),
    address2: string(),
    postalCode: string(),
    city: string(),
    region: string(),
    country: string(),
    // Secondary address
    secondAddress: string(),
    secondAddressUnit: string(),
    secondPostalCode: string(),
    secondCity: string(),
    secondRegion: string(),
    secondCountry: string(),
    isSmoker: boolean().nullable(),
    isDiabetic: boolean().nullable(),
    isHypertensive: boolean().nullable(),
    useBpMeds: boolean().nullable(),
    phones: array().of(
      object().shape({
        id: string().required(),
        value: phoneSchema.required(formatMessage(messages.required)),
        use: string().oneOf(['mobile', 'home', 'work', 'fax']).required(),
        isPrimary: boolean().required(),
      })
    ),
    questionnaireResponses: object()
      .shape({
        ...formHelpers.schemaFields,
      })
      .nullable(),
  });

  async function handleSubmit(values: FormValues) {
    const patient_id = formatPatientId(user?.vitall_patient?.patient_id);
    try {
      if (!patient_id || !patientData) {
        throw new Error('No patient data');
      }
      // Keep the name out of the patient write. PatientRecord inherits
      // snake_case first_name/last_name setters from Contact, and
      // getModelInput assigns every key it is handed, so passing these through
      // rewrites name[0].given as a single element and drops the middle name.
      // That would fire on every save, rename or not, and for viewers
      // canEditName denies. Patient.name is the backend's job: adminChangeName
      // emits FhirEvent.UpdateUser, and fhir.listener writes given as
      // [first_name, ...(middle_name ? [middle_name] : [])].
      const { first_name, middle_name, last_name, ...patientValues } = values;

      const { errors: updateErrors } = await updatePatient({
        input: {
          patient_id,
          patient: {
            ...patientData.getModelInput(patientValues),
            id: patient_id,
          },
        },
      });

      // Mutations run with errorPolicy 'all', so a rejected update (e.g. a 403)
      // resolves rather than throwing. Stop the save here: the remaining calls
      // would write against a patient that was never updated, and closing edit
      // mode below would present the failure as a success. useUpdateFHIRPatient
      // has already surfaced the error.
      if (updateErrors?.length) {
        return;
      }

      // Name lives on the User record, not the FHIR Patient, so it needs its
      // own mutation. Trimmed to match the backend, which trims before
      // validating, so ' Ada ' and 'Ada' are the same edit and neither the
      // comparison below nor the stored value sees the padding.
      const name = {
        first_name: first_name.trim(),
        middle_name: middle_name.trim(),
        last_name: last_name.trim(),
      };

      // Only fired when the fields actually changed and the viewer is allowed
      // to make this specific change.
      if (
        canEditName &&
        (initialValues.first_name !== name.first_name ||
          initialValues.middle_name !== name.middle_name ||
          initialValues.last_name !== name.last_name)
      ) {
        // middle_name goes through as-is, including ''. Coercing '' to
        // undefined drops the key from the payload, so Prisma leaves the
        // stored value alone and clearing a middle name reports success
        // without changing anything.
        const { data: nameData, errors: nameErrors } = await adminChangeName({
          input: { user_id, ...name },
        });

        // Same reason as updatePatient above: errorPolicy 'all' resolves on a
        // 403 rather than throwing, and closing edit mode below would present
        // the failure as a success. useAdminChangeName already surfaced it.
        // success is in the selection set, so check it rather than trusting
        // the absence of errors.
        if (nameErrors?.length || !nameData?.adminChangeName.success) {
          return;
        }
      }

      const {
        isSmoker: initialSmoker,
        isDiabetic: initialDiabetic,
        isHypertensive: initialHypertension,
        useBpMeds: initialBpMed,
        questionnaireResponses: initialQuestResponses,
      } = initialValues;

      // Only touch the scan profile when one of its own fields was edited. A
      // patient with no scan profile yet reads back as all-null, so treating
      // null as "changed" made every demographics save - an address or phone
      // edit included - fire upsertScanProfile.
      const scanDemographicsChanged =
        initialSmoker !== values.isSmoker ||
        initialDiabetic !== values.isDiabetic ||
        initialHypertension !== values.isHypertensive ||
        initialBpMed !== values.useBpMeds;

      if (scanDemographicsChanged) {
        await upsertScanProfile({
          input: {
            patient_id,
            demographics: {
              is_smoker: values.isSmoker ? 0 : 2,
              is_diabetic: values.isDiabetic ?? false,
              is_hypertensive: values.isHypertensive ?? false,
              has_blood_pressure_medication: values.useBpMeds ?? false,
            },
          },
        });
      }

      const questionnaireResponsesHaveChanged = Object.entries(
        values.questionnaireResponses ?? {}
      ).some(([k, v]) => initialQuestResponses?.[k] !== v);

      if (questionnaireResponsesHaveChanged) {
        await createQuestionnaireResponse({
          variables: {
            input: {
              questionnaire_identifier: DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER,
              patient: patient_id,
              responses: getQuestionnaireResponseTemplate(
                patient_id,
                formHelpers.mapValuesToItems(
                  values.questionnaireResponses ?? {}
                )
              ),
            },
          },
        });
      }
      stopEditingRecord();
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    }
  }

  return (
    <Card>
      <ContentLoader loading={loading}>
        <Header is_owner={is_owner} patient={patientData} user={user} />
        {!patientData && (
          <CardContent>
            <Grid container spacing={3}>
              <Grid>
                <DrilldownItem
                  label={formatMessage(messages.email)}
                  readOnly
                  value={user?.email}
                />
              </Grid>
              <Grid>
                <DrilldownItem
                  label={formatMessage(messages.group)}
                  readOnly
                  value={user?.group_name}
                />
              </Grid>
            </Grid>
          </CardContent>
        )}
        {patientData && (
          <CardContent>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({ isSubmitting }) => {
                return (
                  <Form>
                    <Grid container spacing={3}>
                      <DemographicsForm
                        canEditName={canEditName}
                        patient={patientData}
                        user={user}
                      />

                      <ScanProfileForm patient_age={age} />

                      <QuestionnaireForm
                        questionnaire={
                          questionnaireData?.getQuestionnaire.questionnaire
                        }
                      />

                      {isEditing && (
                        <Grid size={{ xs: 12 }}>
                          <RoundedButton
                            disabled={isSubmitting}
                            height={40}
                            type="submit"
                          >
                            {formatMessage(messages.save)}
                          </RoundedButton>
                        </Grid>
                      )}
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </CardContent>
        )}
      </ContentLoader>
    </Card>
  );
}
